/**
 * DetachSkillEffect — Conjuration Tether path active: sever the tether.
 *
 * Instant skill that severs the psychic link between player and shade. On
 * activation, 50% of the player's current HP is spun off as the shade's
 * own independent pool — no longer shared. The shade goes autonomous for
 * 6 seconds, fighting on its own. When the timer expires, whatever HP the
 * shade has left returns to the player as healing. If the shade dies before
 * the timer, the investment is lost — no HP comes back.
 *
 * Cannot be activated without an active shade (canActivate guard). The
 * existing shade is destroyed and replaced by a detached shade with its
 * own HP pool at the same position.
 *
 * Upgrade hooks (resolved at activation via UpgradeEffectResolver, 'detach' scope):
 * - Clean Break (detach_upgrade_1): detachDurationMs max 9000, detachHpPercent max 0.65
 * - Severance (detach_upgrade_2): AoE on detached-shade destruction (handled externally)
 * - Phantom (detach_passive): barrier on successful return (handled externally)
 *
 * Long cooldown tier (2000ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Detach
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ShadeSummonConfig } from './TetherSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DetachDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getBaseDamage(): number;
    getCurrentHp(): number;
    deductHp(amount: number): void;
    heal(amount: number): void;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: ShadeSummonConfig, x: number, y: number): string;
    destroy(instanceId: string): void;
    getShadeIds(): string[];
    getShadePosition(instanceId: string): { x: number; y: number } | null;
    getShadeHp(instanceId: string): number;
    isShadeAlive(instanceId: string): boolean;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface DetachSkillConfig {
  readonly cooldownMs: number;
  /** Duration of the detach phase in ms. */
  readonly detachDurationMs: number;
  /** Fraction of player's current HP transferred to shade (0–1). */
  readonly detachHpPercent: number;
  /** Shade movement speed (integration layer applies px/s). */
  readonly shadeSpeed: number;
  /** How much enemy aggro the detached shade draws (0–1). */
  readonly shadeAggroWeight: number;
  /** Leash radius in tiles for the autonomous-detached shade. */
  readonly shadeLeashRadiusTiles: number;
}

const DEFAULT_CONFIG: DetachSkillConfig = {
  cooldownMs: 2000,            // long cooldown tier
  detachDurationMs: 6000,      // spec: 6 seconds
  detachHpPercent: 0.5,        // spec: 50% of player current HP
  shadeSpeed: 1.0,             // integration layer applies px/s
  shadeAggroWeight: 0.0,       // shade is a projection, draws no aggro
  shadeLeashRadiusTiles: 8,    // autonomous shade has leash range
};

/** Resolver scope — 'detach' for upgrades with enhancesId: 'detach'. */
const RESOLVER_SCOPE = 'detach';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DetachSkillEffect implements SkillEffect {
  readonly skillId = 'detach';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'NO SHADE';

  private readonly deps: DetachDeps;
  private readonly config: DetachSkillConfig;

  private _cooldownRemaining = 0;
  private _detachTimerRemaining = 0;
  private _detaching = false;
  private _detachedShadeId = '';
  private _detachedHp = 0;
  private _activationCount = 0;
  private _returnedHp = 0;
  private _shadeDestroyedDuringDetach = false;
  private _fizzled = false;

  constructor(deps: DetachDeps, config: Partial<DetachSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get detaching(): boolean { return this._detaching; }
  get detachedShadeId(): string { return this._detachedShadeId; }
  get detachedHp(): number { return this._detachedHp; }
  get detachTimerRemaining(): number { return this._detachTimerRemaining; }
  get activationCount(): number { return this._activationCount; }
  get returnedHp(): number { return this._returnedHp; }
  get shadeDestroyedDuringDetach(): boolean { return this._shadeDestroyedDuringDetach; }
  get fizzled(): boolean { return this._fizzled; }

  // --- Integration layer hooks ---

  /**
   * Called by the integration layer when the detached shade is destroyed
   * in combat. No HP returns to the player — the investment is lost.
   */
  onDetachedShadeDestroyed(): void {
    if (!this._detaching) return;
    this._detaching = false;
    this._detachedShadeId = '';
    this._detachTimerRemaining = 0;
    this._shadeDestroyedDuringDetach = true;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._cooldownRemaining > 0) return false;
    if (this._detaching) return false;
    // Requires an active shade to sever
    return this.deps.summons.getShadeIds().length > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not detach
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Get existing shade info before destruction
    const existingShadeIds = this.deps.summons.getShadeIds();
    const firstShadeId = existingShadeIds[0];
    const shadePos = this.deps.summons.getShadePosition(firstShadeId)
      ?? this.deps.player.getPosition();

    // Resolve upgrade values
    const durationMs = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'detachDurationMs')
      ?? this.config.detachDurationMs;
    const hpPercent = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'detachHpPercent')
      ?? this.config.detachHpPercent;

    // Calculate HP transfer
    const playerHp = this.deps.player.getCurrentHp();
    this._detachedHp = Math.floor(playerHp * hpPercent);

    // Destroy all existing shades (sever the tether)
    for (const id of existingShadeIds) {
      this.deps.summons.destroy(id);
    }

    // Deduct HP from player
    this.deps.player.deductHp(this._detachedHp);

    // Spawn detached shade at the former shade's position
    const baseDamage = this.deps.player.getBaseDamage();
    const shadeConfig: ShadeSummonConfig = {
      id: 'detached_shade',
      archetype: 'shade',
      baseHp: this._detachedHp,
      baseDamage,
      baseSpeed: this.config.shadeSpeed,
      leashRadiusTiles: this.config.shadeLeashRadiusTiles,
      aggroWeight: this.config.shadeAggroWeight,
      aiMode: 'autonomous-detached',
      shadeDamageMultiplier: 1.0,
      mirrorDistanceTiles: 0,   // not used in autonomous-detached mode
      sharedHpPool: false,      // independent HP pool — the whole point
    };

    this._detachedShadeId = this.deps.summons.spawn(shadeConfig, shadePos.x, shadePos.y);
    this._detaching = true;
    this._detachTimerRemaining = durationMs;
    this._shadeDestroyedDuringDetach = false;
    this._returnedHp = 0;
    this._activationCount++;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return this._detaching;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick detach timer
    if (this._detaching) {
      this._detachTimerRemaining -= delta;
      if (this._detachTimerRemaining <= 0) {
        this.onDetachTimerExpiry();
      }
    }
  }

  destroy(): void {
    if (this._detaching && this._detachedShadeId) {
      this.deps.summons.destroy(this._detachedShadeId);
    }
    this._detaching = false;
    this._detachedShadeId = '';
    this._cooldownRemaining = 0;
    this._detachTimerRemaining = 0;
  }

  // --- Internal ---

  private onDetachTimerExpiry(): void {
    if (!this._detaching) return;

    // Check if shade is still alive
    if (this.deps.summons.isShadeAlive(this._detachedShadeId)) {
      // Return remaining shade HP to player as healing
      const remainingHp = this.deps.summons.getShadeHp(this._detachedShadeId);
      this._returnedHp = remainingHp;
      this.deps.player.heal(remainingHp);
      // Destroy the shade — it has served its purpose
      this.deps.summons.destroy(this._detachedShadeId);
    }

    this._detaching = false;
    this._detachedShadeId = '';
    this._detachTimerRemaining = 0;
  }
}
